import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { canonicalMessage } from "@/lib/crypto";
import { useAuth } from "@/context/AuthContext";
import BiometricModal from "@/components/BiometricModal";
import VerifyGate from "@/components/VerifyGate";
import AssistantChat from "@/components/AssistantChat";
import { StatusBadge } from "@/pages/Dashboard";
import {
  ArrowLeft, Fingerprint, Link2, CheckCircle2, XCircle,
  CalendarClock, AlertTriangle, Gavel, MessageSquare, Sparkles, Scale,
} from "lucide-react";
import NotifyStatus from "@/components/NotifyStatus";

function Party({ name, email, phone, signed }) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex items-center gap-3">
        <div
          className={`w-9 h-9 rounded-full flex items-center justify-center ${
            signed ? "bg-[#E6F0EB] text-[#2E6B4F]" : "bg-[#F0EBE1] text-[#A3A095]"
          }`}
        >
          <Fingerprint size={18} />
        </div>
        <div>
          <p className="font-medium text-sm">{name || phone || email}</p>
          {phone ? (
            <>
              <p className="text-xs text-[#5C5A54]" data-testid="party-phone">{phone}</p>
              <p className="text-xs text-[#A3A095]">{email}</p>
            </>
          ) : (
            <p className="text-xs text-[#8A877F]">{email}</p>
          )}
        </div>
      </div>
      <span className={`overline ${signed ? "text-[#2E6B4F]" : "text-[#A3A095]"}`}>
        {signed ? "Sealed" : "Awaiting"}
      </span>
    </div>
  );
}

export default function DapDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dap, setDap] = useState(null);
  const [showBio, setShowBio] = useState(false);
  const [busy, setBusy] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [statement, setStatement] = useState("");
  const [medEmail, setMedEmail] = useState("");
  const [showVerify, setShowVerify] = useState(false);
  const tier = user?.identity_verification?.verification_tier || "unverified";

  const load = async () => {
    try {
      const res = await api.get(`/daps/${id}`);
      setDap(res.data);
    } catch (e) {
      toast.error("Dap not found");
      navigate("/dashboard");
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!dap) return <p className="text-[#5C5A54]">Loading…</p>;

  const isCounterparty = user.email === dap.counterparty_email || !!dap.can_claim;
  const isParty = isCounterparty || user.email === dap.creator_email;
  const isMediator = user.email === dap.mediator_email;
  const mediatorActive = dap.mediator_status === "active";
  const myConsent = (dap.mediator_consents || []).includes(user.email);
  const reporter = dap.dispute_opened_by;
  const isAccused = reporter && user.email !== reporter &&
    (user.email === dap.creator_email || user.email === dap.counterparty_email);
  const message = canonicalMessage(dap);
  const overdue =
    dap.deadline &&
    ["sealed", "awaiting_outcome", "disputed"].includes(dap.status) &&
    new Date(dap.deadline) < new Date();

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const onCounterSign = async (signature) => {
    try {
      await api.post(`/daps/${dap.dap_id}/sign`, { signature });
      setShowBio(false);
      toast.success("Dap sealed to the ledger.");
      load();
    } catch (e) {
      setShowBio(false);
      toast.error(e?.response?.data?.detail || "Could not seal");
    }
  };

  const propose = (outcome) =>
    act(() => api.post(`/daps/${dap.dap_id}/outcome/propose`, { outcome }),
      `Marked as ${outcome}. Awaiting the other party's confirmation.`);
  const report = () =>
    act(() => api.post(`/daps/${dap.dap_id}/report-unfulfilled`),
      "Reported as unfulfilled — the dap is now disputed.");
  const concede = () =>
    act(() => api.post(`/daps/${dap.dap_id}/concede`), "You conceded. Recorded as broken for you.");
  const contest = () =>
    act(() => api.post(`/daps/${dap.dap_id}/contest`, { text: statement }).then(() => setStatement("")),
      "Contested. If unresolved after 30 days it becomes 'unresolved'.");
  const resendNotifs = () =>
    act(() => api.post(`/daps/${dap.dap_id}/resend-notifications`), "Notifications re-sent to the counterparty.");
  const confirm = () =>
    act(() => api.post(`/daps/${dap.dap_id}/outcome/confirm`), "Outcome confirmed and recorded.");
  const decline = () => act(() => api.post(`/daps/${dap.dap_id}/decline`), "Dap declined.");

  const openDispute = () =>
    act(() => api.post(`/daps/${dap.dap_id}/dispute/open`, { statement }).then(() => {
      setDisputeOpen(false); setStatement("");
    }), "Dispute opened.");
  const addStatement = () =>
    act(() => api.post(`/daps/${dap.dap_id}/dispute/statement`, { text: statement }).then(() => setStatement("")),
      "Statement added.");
  const proposeResolution = (outcome) =>
    act(() => api.post(`/daps/${dap.dap_id}/dispute/resolve`, { outcome }),
      `Proposed resolution: ${outcome}.`);
  const confirmResolution = () =>
    act(() => api.post(`/daps/${dap.dap_id}/dispute/confirm`), "Dispute resolved.");
  const inviteMediator = () =>
    act(() => api.post(`/daps/${dap.dap_id}/dispute/invite-mediator`, { email: medEmail }).then(() => setMedEmail("")),
      "Mediator invited.");
  const castVerdict = (outcome) =>
    act(() => api.post(`/daps/${dap.dap_id}/dispute/mediate`, { outcome }),
      `Your decision was recorded: ${outcome}.`);
  const consentMediator = () =>
    act(() => api.post(`/daps/${dap.dap_id}/dispute/mediator/consent`),
      "You consented to the mediator.");

  const canConfirmOutcome = dap.status === "awaiting_outcome" && dap.proposed_by !== user.email;
  const canConfirmResolution =
    dap.status === "disputed" && dap.proposed_outcome && dap.proposed_by !== user.email;

  return (
    <div data-testid="dap-detail-page" className="max-w-2xl mx-auto">
      <button
        onClick={() => navigate("/dashboard")}
        data-testid="back-btn"
        className="flex items-center gap-2 text-[#5C5A54] hover:text-[#1A1A1A] mb-8"
      >
        <ArrowLeft size={16} /> Dashboard
      </button>

      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="overline text-[#E07A5F]">Handshake Agreement</p>
          <h1 className="font-serif-dl text-4xl tracking-tight mt-2">{dap.title}</h1>
        </div>
        <StatusBadge status={dap.status} />
      </div>

      {dap.deadline && (
        <div
          data-testid="deadline-row"
          className={`mt-4 inline-flex items-center gap-2 text-sm rounded-md px-3 py-1.5 ${
            overdue ? "bg-[#F5E6E6] text-[#9C3A3A]" : "bg-[#F0EBE1] text-[#5C5A54]"
          }`}
        >
          {overdue ? <AlertTriangle size={15} /> : <CalendarClock size={15} />}
          {overdue ? "Overdue — " : "Due "}
          {new Date(dap.deadline).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
        </div>
      )}

      <div className="mt-6 bg-white border border-[#DEDBD3] rounded-lg p-6 sm:p-8">
        <p className="overline text-[#5C5A54] mb-2">Terms & understanding</p>
        <p className="text-[#1A1A1A] leading-relaxed whitespace-pre-wrap">{dap.terms}</p>

        <div className="mt-6 border-t border-[#DEDBD3] pt-4 divide-y divide-[#F0EBE1]">
          <Party name={dap.creator_name} email={dap.creator_email} phone={dap.creator_phone_mask} signed={true} />
          <Party
            name={dap.counterparty_name}
            email={dap.counterparty_email || "Awaiting claim"}
            phone={dap.counterparty_phone_mask || dap.counterparty_phone}
            signed={dap.status !== "pending" && dap.status !== "declined"}
          />
        </div>

        {dap.block_hash && (
          <div className="mt-4 bg-[#F0EBE1]/60 rounded-md p-4">
            <div className="flex items-center gap-2 text-[#1B4D3E]">
              <Link2 size={15} />
              <span className="overline">Ledger Block #{dap.block_index}</span>
            </div>
            <p className="font-mono-dl text-xs text-[#5C5A54] mt-2 break-all" data-testid="dap-block-hash">
              {dap.block_hash}
            </p>
          </div>
        )}
      </div>

      {/* Counterparty notification delivery status (creator view) */}
      {user.email === dap.creator_email && (
        <NotifyStatus delivery={dap.notify_delivery} busy={busy} onResend={resendNotifs} />
      )}

      {/* Dispute thread */}
      {dap.status === "disputed" && (
        <div className="mt-6 bg-[#FBEEE6] border border-[#B5541F]/25 rounded-lg p-6" data-testid="dispute-panel">
          <div className="flex items-center gap-2 text-[#B5541F]">
            <Gavel size={18} />
            <p className="overline">Under Dispute</p>
          </div>
          <p className="text-sm text-[#8f6b52] mt-2">
            {reporter} reported this dap unfulfilled. It is not recorded as broken while under dispute.
          </p>

          {isAccused && (
            <div className="mt-4 bg-white rounded-lg border border-[#Eadfd4] p-4" data-testid="accused-actions">
              <p className="text-sm font-medium mb-3">You were reported. How do you respond?</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  data-testid="concede-btn"
                  disabled={busy}
                  onClick={concede}
                  className="py-2.5 rounded-lg bg-[#F5E6E6] text-[#9C3A3A] border border-[#9C3A3A]/20 hover:bg-[#f0dada] text-sm"
                >
                  Concede (broken)
                </button>
                <button
                  data-testid="contest-btn"
                  disabled={busy || statement.trim().length < 2}
                  onClick={contest}
                  className="py-2.5 rounded-lg bg-[#1B4D3E] text-white hover:bg-[#143A2F] disabled:opacity-50 text-sm"
                >
                  Contest with statement
                </button>
              </div>
              <p className="text-[11px] text-[#A3A095] mt-2">
                To contest, add your statement below first. Contested disputes unresolved after 30 days weigh half.
              </p>
            </div>
          )}
          <div className="mt-4 space-y-3">
            {(dap.dispute_statements || []).map((s, i) => (
              <div key={`${s.email}-${s.at}-${i}`} className="bg-white rounded-md p-3 border border-[#Eadfd4]">
                <p className="text-xs font-semibold text-[#5C5A54]">
                  {s.name}
                  {s.role === "mediator" && (
                    <span className="ml-1 text-[9px] uppercase tracking-wider bg-[#FBEEE6] text-[#B5541F] px-1.5 py-0.5 rounded">Mediator</span>
                  )}
                  <span className="text-[#A3A095] font-normal"> · {new Date(s.at).toLocaleString()}</span>
                </p>
                <p className="text-sm mt-1 text-[#1A1A1A] whitespace-pre-wrap">{s.text}</p>
              </div>
            ))}
          </div>

          {(isParty || isMediator) && (
            <div className="mt-4">
              <textarea
                data-testid="dispute-statement-input"
                value={statement}
                onChange={(e) => setStatement(e.target.value)}
                rows={2}
                placeholder={isMediator ? "Add a mediator note…" : "Add your statement…"}
                className="w-full bg-white border border-[#Eadfd4] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#B5541F] resize-none"
              />
              <button
                data-testid="add-statement-btn"
                disabled={busy || statement.trim().length < 2}
                onClick={addStatement}
                className="mt-2 flex items-center gap-2 text-sm text-[#B5541F] hover:text-[#8f421a] disabled:opacity-50"
              >
                <MessageSquare size={15} /> Add statement
              </button>
            </div>
          )}

          <div className="mt-5 border-t border-[#B5541F]/15 pt-4" data-testid="mediator-section">
            {dap.mediator_email ? (
              <>
                <div className="flex items-center gap-2 text-[#B5541F] mb-2">
                  <Scale size={15} />
                  <p className="overline">Neutral mediator</p>
                </div>
                <p className="text-sm text-[#5C5A54]">{dap.mediator_name || dap.mediator_email}</p>
                <p className="text-xs mt-1 text-[#8f6b52]" data-testid="mediator-status">
                  {mediatorActive
                    ? "Both parties consented — the mediator can now decide."
                    : "Proposed — both parties must consent before the mediator can decide."}
                </p>
                {isParty && !isMediator && !mediatorActive && (
                  myConsent ? (
                    <p className="text-xs text-[#8f6b52] mt-3" data-testid="mediator-awaiting-consent">
                      You consented. Waiting for the other party to consent.
                    </p>
                  ) : (
                    <button
                      data-testid="consent-mediator-btn"
                      disabled={busy}
                      onClick={consentMediator}
                      className="mt-3 px-4 py-2 bg-[#B5541F] text-white rounded-md text-sm hover:bg-[#8f421a] disabled:opacity-50 inline-flex items-center gap-2"
                    >
                      <Scale size={15} /> Consent to this mediator
                    </button>
                  )
                )}
                {isMediator && (
                  <div className="mt-3">
                    {mediatorActive ? (
                      <>
                        <p className="text-xs text-[#8f6b52] mb-2">
                          Both parties consented. After hearing both sides, cast the deciding call — it's final.
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            data-testid="mediate-honored-btn"
                            disabled={busy}
                            onClick={() => castVerdict("honored")}
                            className="py-3 rounded-lg bg-[#E6F0EB] text-[#2E6B4F] border border-[#2E6B4F]/20 hover:bg-[#d9ebe0] flex items-center justify-center gap-2"
                          >
                            <CheckCircle2 size={18} /> Honored
                          </button>
                          <button
                            data-testid="mediate-broken-btn"
                            disabled={busy}
                            onClick={() => castVerdict("broken")}
                            className="py-3 rounded-lg bg-[#F5E6E6] text-[#9C3A3A] border border-[#9C3A3A]/20 hover:bg-[#f0dada] flex items-center justify-center gap-2"
                          >
                            <XCircle size={18} /> Broken
                          </button>
                        </div>
                      </>
                    ) : (
                      <p className="text-xs text-[#8f6b52]" data-testid="mediator-waiting">
                        Waiting for both parties to consent before you can decide.
                      </p>
                    )}
                  </div>
                )}
              </>
            ) : isParty ? (
              <>
                <div className="flex items-center gap-2 text-[#B5541F] mb-2">
                  <Scale size={15} />
                  <p className="overline">Invite a neutral mediator</p>
                </div>
                <p className="text-xs text-[#8f6b52] mb-3">
                  Can't agree? Invite a trusted community member to hear both sides and make the deciding call.
                </p>
                <div className="flex gap-2">
                  <input
                    data-testid="mediator-email-input"
                    type="email"
                    value={medEmail}
                    onChange={(e) => setMedEmail(e.target.value)}
                    placeholder="mediator@email.com"
                    className="flex-1 bg-white border border-[#Eadfd4] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#B5541F]"
                  />
                  <button
                    data-testid="invite-mediator-btn"
                    disabled={busy || !medEmail.includes("@")}
                    onClick={inviteMediator}
                    className="px-4 py-2 bg-[#B5541F] text-white rounded-md text-sm hover:bg-[#8f421a] disabled:opacity-50 flex items-center gap-2 shrink-0"
                  >
                    <Scale size={15} /> Invite
                  </button>
                </div>
              </>
            ) : null}
          </div>

          <div className="mt-5 border-t border-[#B5541F]/15 pt-4">
            <p className="overline text-[#B5541F] mb-3">Resolve by mutual agreement</p>
            {canConfirmResolution ? (
              <button
                data-testid="confirm-resolution-btn"
                disabled={busy}
                onClick={confirmResolution}
                className="w-full py-3 bg-[#1B4D3E] text-white rounded-lg hover:bg-[#143A2F] transition-colors"
              >
                Agree — resolve as {dap.proposed_outcome}
              </button>
            ) : dap.proposed_outcome ? (
              <p className="text-sm text-[#8f6b52]">
                You proposed resolving as <b>{dap.proposed_outcome}</b>. Awaiting the other party.
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <button
                  data-testid="resolve-honored-btn"
                  disabled={busy}
                  onClick={() => proposeResolution("honored")}
                  className="py-3 rounded-lg bg-[#E6F0EB] text-[#2E6B4F] border border-[#2E6B4F]/20 hover:bg-[#d9ebe0] flex items-center justify-center gap-2"
                >
                  <CheckCircle2 size={18} /> Honored
                </button>
                <button
                  data-testid="resolve-broken-btn"
                  disabled={busy}
                  onClick={() => proposeResolution("broken")}
                  className="py-3 rounded-lg bg-[#F5E6E6] text-[#9C3A3A] border border-[#9C3A3A]/20 hover:bg-[#f0dada] flex items-center justify-center gap-2"
                >
                  <XCircle size={18} /> Broken
                </button>
              </div>
            )}
          </div>

          <div className="mt-5 border-t border-[#B5541F]/15 pt-4">
            <div className="flex items-center gap-2 text-[#B5541F] mb-3">
              <Sparkles size={16} />
              <p className="overline">Advisor — neutral suggestion</p>
            </div>
            <AssistantChat
              mode="dispute"
              sessionId={`dispute-${dap.dap_id}`}
              dapId={dap.dap_id}
              welcome="I can read the terms and both statements and suggest a fair way forward."
              placeholder="Ask for a fair, neutral suggestion…"
              heightClass="h-56"
              quickActions={[{
                label: "Suggest a fair resolution",
                message: "Based on the agreed terms and both statements, suggest a fair resolution and whether it leans honored or broken.",
              }]}
            />
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="mt-6 space-y-3">
        {dap.status === "pending" && isCounterparty && (
          <>
            <button
              data-testid="counter-seal-btn"
              onClick={() => (tier === "unverified" ? setShowVerify(true) : setShowBio(true))}
              className="w-full py-4 bg-[#E07A5F] hover:bg-[#C96A51] text-white rounded-lg font-medium tracking-wide flex items-center justify-center gap-2 transition-colors"
            >
              <Fingerprint size={18} /> Seal this dap with your biometric
            </button>
            <button
              data-testid="decline-btn"
              disabled={busy}
              onClick={decline}
              className="w-full py-3 border border-[#DEDBD3] text-[#9C3A3A] rounded-lg hover:bg-[#F5E6E6] transition-colors"
            >
              Decline
            </button>
          </>
        )}

        {dap.status === "pending" && !isCounterparty && (
          <div className="bg-[#F9F3E8] border border-[#D4A373]/30 rounded-lg p-5 text-[#B07B2E] text-sm text-center">
            Waiting for {dap.counterparty_email || dap.counterparty_phone || "the other party"} to seal their side.
          </div>
        )}

        {dap.status === "sealed" && isParty && (
          <div className="bg-white border border-[#DEDBD3] rounded-lg p-6">
            <p className="overline text-[#5C5A54] mb-4">Fulfillment — was this dap kept?</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                data-testid="mark-honored-btn"
                disabled={busy}
                onClick={() => propose("honored")}
                className="py-3 rounded-lg bg-[#E6F0EB] text-[#2E6B4F] border border-[#2E6B4F]/20 hover:bg-[#d9ebe0] flex items-center justify-center gap-2 transition-colors"
              >
                <CheckCircle2 size={18} /> Honored
              </button>
              <button
                data-testid="mark-broken-btn"
                disabled={busy}
                onClick={report}
                className="py-3 rounded-lg bg-[#FBEEE6] text-[#B5541F] border border-[#B5541F]/20 hover:bg-[#f6e2d6] flex items-center justify-center gap-2 transition-colors"
              >
                <XCircle size={18} /> Report unfulfilled
              </button>
            </div>
            <p className="mt-3 text-xs text-[#8A877F]">
              Reporting a dap as unfulfilled opens a dispute — it isn't recorded as broken until resolved.
            </p>
          </div>
        )}

        {dap.status === "awaiting_outcome" && (
          <div className="bg-[#F9F3E8] border border-[#D4A373]/30 rounded-lg p-5">
            <p className="text-sm text-[#B07B2E]">
              <span className="font-semibold">{dap.proposed_by}</span> proposed this dap
              be marked <span className="font-semibold">{dap.proposed_outcome}</span>.
            </p>
            {canConfirmOutcome ? (
              <div className="mt-4 space-y-2">
                <button
                  data-testid="confirm-outcome-btn"
                  disabled={busy}
                  onClick={confirm}
                  className="w-full py-3 bg-[#1B4D3E] text-white rounded-lg hover:bg-[#143A2F] transition-colors"
                >
                  Confirm — mark as {dap.proposed_outcome}
                </button>
                <button
                  data-testid="raise-dispute-btn"
                  onClick={report}
                  className="w-full py-2.5 border border-[#B5541F]/30 text-[#B5541F] rounded-lg hover:bg-[#FBEEE6] flex items-center justify-center gap-2 text-sm transition-colors"
                >
                  <Gavel size={16} /> Disagree — report unfulfilled
                </button>
              </div>
            ) : (
              <p className="mt-2 text-xs text-[#8A877F]">Awaiting the other party to confirm.</p>
            )}
          </div>
        )}

        {(dap.status === "honored" || dap.status === "broken") && (
          <div
            className={`rounded-lg p-6 text-center ${
              dap.status === "honored" ? "bg-[#E6F0EB] text-[#2E6B4F]" : "bg-[#F5E6E6] text-[#9C3A3A]"
            }`}
          >
            <p className="font-serif-dl text-2xl">
              This dap was {dap.status === "honored" ? "honored" : "broken"}.
            </p>
          </div>
        )}
      </div>

      {/* Raise dispute modal */}
      {disputeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4" data-testid="raise-dispute-modal">
          <div className="bg-white rounded-2xl border border-[#DEDBD3] w-full max-w-md p-8 animate-fade-up">
            <div className="flex items-center gap-2 text-[#B5541F]">
              <Gavel size={18} />
              <p className="overline">Raise a Dispute</p>
            </div>
            <h3 className="font-serif-dl text-2xl tracking-tight mt-2">State your case</h3>
            <p className="text-sm text-[#5C5A54] mt-1">
              Both parties can add statements. The dap resolves only by mutual agreement.
            </p>
            <textarea
              data-testid="open-dispute-input"
              value={statement}
              onChange={(e) => setStatement(e.target.value)}
              rows={4}
              placeholder="What went wrong, in your words…"
              className="mt-4 w-full border border-[#DEDBD3] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#B5541F] resize-none"
            />
            <div className="mt-4 flex gap-3">
              <button
                onClick={() => setDisputeOpen(false)}
                className="flex-1 py-3 border border-[#DEDBD3] rounded-lg text-[#5C5A54] hover:bg-[#F9F8F6]"
              >
                Cancel
              </button>
              <button
                data-testid="open-dispute-btn"
                disabled={busy || statement.trim().length < 2}
                onClick={openDispute}
                className="flex-1 py-3 bg-[#B5541F] text-white rounded-lg hover:bg-[#8f421a] disabled:opacity-50"
              >
                Open dispute
              </button>
            </div>
          </div>
        </div>
      )}

      <BiometricModal
        open={showBio}
        onClose={() => setShowBio(false)}
        message={message}
        actionLabel="Seal my word"
        onComplete={onCounterSign}
      />
    </div>
  );
}
